import {
  Button,
  ColorSwatchPicker,
  ColorSwatchPickerItem,
  Dialog,
  DialogTrigger,
  parseColor,
  Popover,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { css, cx } from "../../../../styled-system/css";
import { grid } from "../../../../styled-system/patterns";
import type { SystemStyleObject } from "../../../../styled-system/types";
import { popoverStyles } from "../styles/styles";
import { ColorSwatch } from "./ColorSwatch";

const DEFAULT_COLORS = [
  "#C63066",
  "#EE548C",
  "#9D7EB8",
  "#623F7F",
  "#AF4B99",
  "#0fc398",
  "#eebd22",
  "#f03c67",
  "#3498db",
  "#2ecc71",
  "#e74c3c",
  "#9b59b6",
];

interface ColorPickerProps {
  value: string | null;
  onChange: (color: string | null) => void;
  css?: SystemStyleObject;
}

export function ColorPicker({ value, onChange, css: cssProp }: ColorPickerProps) {
  const { t } = useTranslation(["common"]);
  return (
    <DialogTrigger>
      <Button
        className={css(
          {
            all: "unset",
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "full",
            padding: "xsmall",
            _focusVisible: {
              outline: "2px solid {colors.trellisPalette.3}",
              outlineOffset: "2px",
            },
          },
          cssProp,
        )}
        aria-label={t(($) => $.common["Select color"])}
      >
        <ColorSwatch size="medium" color={value} />
      </Button>
      <Popover
        className={cx(
          popoverStyles,
          css({
            padding: "medium",
            boxShadow: "card",
          }),
        )}
        offset={4}
      >
        <Dialog
          className={css({
            outline: "none",
          })}
        >
          {({ close }) => (
            <ColorSwatchPicker
              ref={(el) => el?.focus()}
              value={value ? parseColor(value) : undefined}
              onChange={(color) => {
                onChange(color.toString("hex"));
                close();
              }}
              className={grid({ gap: "small", columns: 4 })}
            >
              {DEFAULT_COLORS.map((color) => (
                <ColorSwatchPickerItem
                  key={color}
                  className={css({
                    cursor: "pointer",
                    borderRadius: "light",
                    width: "32px",
                    height: "32px",
                    _selected: {
                      outline: "2px solid {colors.trellisPalette.3}",
                      outlineOffset: "2px",
                    },
                    _focusVisible: {
                      outline: "2px solid {colors.trellisPalette.3}",
                      outlineOffset: "2px",
                    },
                    _hover: { backgroundColor: "lightgrey/50" },
                  })}
                  color={color}
                >
                  <ColorSwatch
                    shape="square"
                    css={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </ColorSwatchPickerItem>
              ))}
            </ColorSwatchPicker>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
